import ModuleContext from 'CMFW/context/ModuleContext';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { IIllustration } from '../../types/illustration';
import { faDownload, faMoon, faTrash, faUpload, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalWrapper } from '../../components/ModalWrapper';
import { Button, CircularProgress, Tooltip, FormControlLabel, Radio, RadioGroup, Menu, MenuItem } from '@material-ui/core';
import { EditorRow } from '../../components/common/styles';
import FileUpload from '../../CMFW/input/FileUpload';
import IllustrationModel from '../../models/IllustrationModel';
import VocabularyModel from '../../models/VocabularyModel';
import {
  isSharedIllustration,
  getSharedFromText,
  ILLUSTRATION_STATUS_PNG_UPLOADED,
  ILLUSTRATION_STATUS_ENHANCING,
  ILLUSTRATION_STATUS_DONE,
} from '../../types/illustrationStatus';
import { getIllustrationColor } from '../../constants/illustrationStageColors';
import { DarkMode, BG_COLOR } from '../AIIllustration/types/darkMode';

enum PNGStage {
  ORIGINAL = 1,
  ENHANCED = 2,
}

const PNG_STAGE_INFO: Record<PNGStage, { label: string; tooltip: string }> = {
  [PNGStage.ORIGINAL]: {
    label: 'O',
    tooltip: 'Original',
  },
  [PNGStage.ENHANCED]: {
    label: 'F',
    tooltip: 'Final',
  },
};

type UploadResponse = { status: number; error?: string };

enum UploadType {
  ORIGINAL = 'original',
  ENHANCED = 'enhanced',
}

enum DeleteType {
  ALL = 'all',
  FINAL = 'final',
}

interface IllustrationPNGProps {
  /** IIllustration data - if not provided, will be read from module context */
  illustration?: IIllustration;
  /** Custom upload handler - if not provided, uses default VocabularyModel upload */
  onUpload?: (file: File, isEnhanced: boolean) => Promise<UploadResponse | undefined>;
  /** Custom delete handler - if not provided, uses default VocabularyModel delete */
  onDelete?: () => Promise<void>;
  /** Callback to reload data after upload/delete */
  onReload?: () => void;
  /** Show 2-stage workflow indicators (default: true) */
  showStages?: boolean;
  /** Custom upload modal title */
  uploadTitle?: string;
  /** Custom upload modal description */
  uploadDescription?: string;
}

/**
 * PNG-only illustration viewer for Vocabulary
 * Supports 2-stage PNG workflow: original → enhanced (with alpha from OpenAI)
 * Handles synonym illustration sharing
 *
 * Can be used in two modes:
 * 1. Default mode (no props): Works with VocabularyModel context, shows stages and AI score
 * 2. Custom mode (with props): Works with any illustration data, custom handlers
 */
const IllustrationPNG: React.FC<IllustrationPNGProps> = ({
  illustration: propIllustration,
  onUpload,
  onDelete,
  onReload,
  showStages = true,
  uploadTitle,
  uploadDescription,
}) => {
  const module = useContext(ModuleContext) as VocabularyModel | null;

  // Use prop illustration if provided, otherwise get from context
  const illustration: IIllustration | undefined = propIllustration ?? module?.getItemValue('illustration');

  const illustrationModel = new IllustrationModel();

  // Determine the best available stage (2→1) based on actual status
  const getBestAvailableStage = (): PNGStage => {
    if (!illustration) return PNGStage.ENHANCED;
    if (illustration.illustration >= ILLUSTRATION_STATUS_DONE) return PNGStage.ENHANCED;
    return PNGStage.ORIGINAL;
  };

  const [pngStage, setPngStage] = useState(getBestAvailableStage());
  const [darkMode, setDarkMode] = useState(DarkMode.LIGHT);
  const [showUpload, setShowUpload] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [filePNG, setFilePNG] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<UploadType>(UploadType.ORIGINAL);
  const [deleteType, setDeleteType] = useState<DeleteType>(DeleteType.ALL);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [message, setMessage] = useState('');
  const [cacheBust, setCacheBust] = useState(0);

  const getFile = () => {
    if (!illustration) return undefined;

    // For single-PNG mode (no stages), just return the png field directly
    if (!showStages) {
      return illustration.png || null;
    }

    // Return file based on selected stage, with fallback logic
    if (pngStage === PNGStage.ENHANCED && illustration.enhancedPng) {
      return illustration.enhancedPng;
    } else if (pngStage === PNGStage.ORIGINAL && illustration.originalPng) {
      return illustration.originalPng;
    }

    // Fallback: prefer enhanced > original
    return illustration.enhancedPng || illustration.originalPng;
  };

  const setStage = (stage: PNGStage) => {
    setPngStage(stage);
  };

  const toggleDarkMode = () => {
    setDarkMode(darkMode === DarkMode.LIGHT ? DarkMode.DARK : DarkMode.LIGHT);
  };

  const downloadFile = (file: string) => {
    const fileLink = document.createElement('a');
    fileLink.href = file;
    fileLink.download = file.split('/').pop() as string;
    document.body.appendChild(fileLink);
    fileLink.click();
    document.body.removeChild(fileLink);
  };

  const hasSvg = !!illustration?.originalSvg;
  const hasOriginalPng = illustration ? illustration.illustration >= ILLUSTRATION_STATUS_PNG_UPLOADED : false;
  const hasFinalPng = illustration ? illustration.illustration >= ILLUSTRATION_STATUS_DONE : false;

  const handleShowDownload = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!illustration) return;

    // Single-PNG mode (books, sentences, vocCategories): just download illustration.png
    if (!showStages) {
      const file = getFile();
      if (file) downloadFile(file);
      return;
    }

    const count = [hasSvg, hasOriginalPng, hasFinalPng].filter(Boolean).length;

    // If only one file, download directly
    if (count <= 1) {
      downloadFile((hasFinalPng && illustration.enhancedPng) || (hasOriginalPng && illustration.originalPng) || illustration.originalSvg || '');
      return;
    }

    setDownloadAnchor(event.currentTarget as unknown as HTMLElement);
  };

  const handleShowUpload = () => {
    // Block uploads for shared illustrations (synonym children)
    if (illustration && isSharedIllustration(illustration)) {
      alert('Cannot upload illustration for synonym child. Upload to the parent synonym instead.');
      return;
    }
    setUploadType(canUploadFinal ? UploadType.ENHANCED : UploadType.ORIGINAL);
    setShowUpload(true);
  };

  const closeShowUpload = () => {
    setShowUpload(false);
    setFilePNG(null);
    setUploadType(UploadType.ORIGINAL);
    setMessage('');
  };

  // Check if upload is for enhanced (final) stage
  const isEnhancedUpload = uploadType === UploadType.ENHANCED;

  const handleShowDelete = () => {
    setShowDelete(true);
  };

  const closeShowDelete = () => {
    setShowDelete(false);
  };

  const handleFileChosenPNG = (file: File) => {
    setFilePNG(file);
  };

  const handleUploadClick = async () => {
    if (!filePNG || !illustration) {
      return;
    }
    setIsUploading(true);
    setMessage('');

    // Use custom upload handler if provided, otherwise use default
    const response = onUpload
      ? await onUpload(filePNG, isEnhancedUpload)
      : await illustrationModel.uploadPNG(illustration, filePNG, isEnhancedUpload);

    if (!response) {
      setMessage('Something went wrong, try again.');
    } else {
      if (response.status === 1) {
        closeShowUpload();
        setCacheBust(Date.now());
        // Use custom reload callback if provided, otherwise use module reload
        if (onReload) {
          onReload();
        } else if (module) {
          module.reloadItem();
        }
      } else {
        setMessage(`<p><b>File with errors:</b></p><p>${response.error}</p>`);
      }
    }
    setIsUploading(false);
  };

  const handleDeleteClick = async (type: DeleteType = DeleteType.ALL) => {
    if (illustration) {
      setDeleteType(type);
      setIsDeleting(true);
      // Use custom delete handler if provided, otherwise use default
      if (onDelete) {
        await onDelete();
      } else if (type === DeleteType.FINAL) {
        await illustrationModel.deleteEnhancedPNG(illustration);
      } else {
        await illustrationModel.deletePNG(illustration);
      }
      closeShowDelete();
      // Use custom reload callback if provided, otherwise use module reload
      if (onReload) {
        onReload();
      } else if (module) {
        module.reloadItem();
      }
      setIsDeleting(false);
    }
  };

  if (!illustration) {
    return null;
  }

  const sharedText = getSharedFromText(illustration);
  const isShared = isSharedIllustration(illustration);
  const imageFile = getFile();
  const hasImageFile = !!imageFile;
  const canUploadFinal = illustration.illustration === ILLUSTRATION_STATUS_PNG_UPLOADED || illustration.illustration === ILLUSTRATION_STATUS_ENHANCING;

  // Check if this is an "enhanced only" upload (enhanced exists but no original)
  const isEnhancedOnly = !!illustration.enhancedPng && !illustration.originalPng;

  // Determine stage color based on illustration status
  const getStageColor = (stage: PNGStage): string => {
    // Map illustration status to colors - use actual status value, not just path existence
    if (stage === PNGStage.ORIGINAL && illustration.originalPng) {
      return getIllustrationColor(ILLUSTRATION_STATUS_PNG_UPLOADED);
    }
    if (stage === PNGStage.ENHANCED && illustration.illustration >= ILLUSTRATION_STATUS_DONE) {
      return getIllustrationColor(ILLUSTRATION_STATUS_DONE);
    }
    return '#cccccc'; // Not completed - gray
  };

  // Default upload modal text based on upload type
  const getUploadTitle = () => {
    if (uploadTitle) return uploadTitle;
    return uploadType === UploadType.ORIGINAL ? 'Upload Original PNG (Stage 1)' : 'Upload Enhanced PNG (Stage 2)';
  };

  const getUploadDescription = () => {
    if (uploadDescription) return uploadDescription;
    if (uploadType === UploadType.ORIGINAL) {
      return (
        <>
          Upload the <strong>original PNG</strong> image. This is stage 1 of the 2-stage workflow:
          <br />1 → Original → 2 → Enhanced (AI with alpha)
        </>
      );
    }
    return (
      <>
        Upload the <strong>enhanced PNG</strong> image directly. This skips AI enhancement and marks the illustration as complete.
      </>
    );
  };

  return (
    <Wrapper>
      {hasImageFile && imageFile ? (
        <ImgPng src={cacheBust ? `${imageFile}?v=${cacheBust}` : imageFile} style={{ backgroundColor: BG_COLOR[darkMode] }} />
      ) : (
        <NoImageText>No illustration</NoImageText>
      )}
      <Footer>
        {showStages && hasImageFile && (
          <StageIndicator>
            <Tooltip title={isEnhancedOnly ? 'Not available (uploaded as enhanced)' : PNG_STAGE_INFO[PNGStage.ORIGINAL].tooltip} arrow>
              <StageBox
                active={pngStage === PNGStage.ORIGINAL}
                completed={!!illustration.originalPng}
                stageColor={getStageColor(PNGStage.ORIGINAL)}
                disabled={isEnhancedOnly}
                onClick={() => !isEnhancedOnly && illustration.originalPng && setStage(PNGStage.ORIGINAL)}
              >
                {PNG_STAGE_INFO[PNGStage.ORIGINAL].label}
              </StageBox>
            </Tooltip>
            <Tooltip title={PNG_STAGE_INFO[PNGStage.ENHANCED].tooltip} arrow>
              <StageBox
                active={pngStage === PNGStage.ENHANCED}
                completed={!!illustration.enhancedPng}
                stageColor={getStageColor(PNGStage.ENHANCED)}
                onClick={() => illustration.enhancedPng && setStage(PNGStage.ENHANCED)}
              >
                {PNG_STAGE_INFO[PNGStage.ENHANCED].label}
              </StageBox>
            </Tooltip>
          </StageIndicator>
        )}
        {hasImageFile && <Icon icon={faMoon} onClick={toggleDarkMode} />}
        {!isShared && hasImageFile && <Icon icon={faDownload} onClick={handleShowDownload} />}
        {!isShared && (!hasImageFile || canUploadFinal) && <Icon icon={faUpload} onClick={handleShowUpload} />}
        {!isShared && hasImageFile && <Icon icon={faTrash} onClick={handleShowDelete} />}
        {showStages && isShared && sharedText && (
          <Tooltip title={sharedText} arrow>
            <SharedIcon icon={faShareAlt} />
          </Tooltip>
        )}
      </Footer>
      {showUpload && (
        <ModalWrapper
          onClose={closeShowUpload}
          title={getUploadTitle()}
          width={380}
          height={280}
        >
          {showStages && !canUploadFinal && (
            <RadioGroup
              row
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value as UploadType)}
              style={{ marginBottom: 8 }}
            >
              <FormControlLabel
                value={UploadType.ORIGINAL}
                control={<Radio size="small" color="primary" />}
                label="Original (Stage 1)"
              />
              <FormControlLabel
                value={UploadType.ENHANCED}
                control={<Radio size="small" color="primary" />}
                label="Enhanced (Stage 2)"
              />
            </RadioGroup>
          )}
          <UploadDescriptionStyled>
            {getUploadDescription()}
          </UploadDescriptionStyled>
          <EditorRowStyled>
            <span>Select PNG file: </span>
            <FileUploadStyled handleFile={handleFileChosenPNG} />
          </EditorRowStyled>
          <EditorRowStyled>
            <Button variant="contained" size="small" disabled={!filePNG || isUploading} onClick={handleUploadClick}>
              Upload
            </Button>
            {isUploading && <CircularProgress size={20} />}
          </EditorRowStyled>
          <p dangerouslySetInnerHTML={{ __html: message }} />
        </ModalWrapper>
      )}
      <Menu
        anchorEl={downloadAnchor}
        open={Boolean(downloadAnchor)}
        onClose={() => setDownloadAnchor(null)}
      >
        {hasSvg && (
          <MenuItem onClick={() => { downloadFile(illustration.originalSvg!); setDownloadAnchor(null); }}>
            Original SVG
          </MenuItem>
        )}
        {hasOriginalPng && illustration.originalPng && (
          <MenuItem onClick={() => { downloadFile(illustration.originalPng!); setDownloadAnchor(null); }}>
            Original PNG
          </MenuItem>
        )}
        {hasFinalPng && illustration.enhancedPng && (
          <MenuItem onClick={() => { downloadFile(illustration.enhancedPng!); setDownloadAnchor(null); }}>
            Final PNG
          </MenuItem>
        )}
      </Menu>
      {showDelete && (
        <ModalWrapper onClose={closeShowDelete} title={'Delete Image'} width={200} height={illustration.enhancedPng && illustration.originalPng ? 120 : 80}>
          {illustration.enhancedPng && illustration.originalPng ? (
            <>
              <DeleteButtonRow>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  disabled={isDeleting}
                  onClick={() => handleDeleteClick(DeleteType.ALL)}
                >
                  Delete All
                </Button>
                {isDeleting && deleteType === DeleteType.ALL && <CircularProgress size={20} />}
              </DeleteButtonRow>
              <DeleteButtonRow>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={isDeleting}
                  onClick={() => handleDeleteClick(DeleteType.FINAL)}
                >
                  Delete Final
                </Button>
                {isDeleting && deleteType === DeleteType.FINAL && <CircularProgress size={20} />}
              </DeleteButtonRow>
            </>
          ) : (
            <DeleteButtonRow>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                disabled={isDeleting}
                onClick={() => handleDeleteClick(DeleteType.ALL)}
              >
                Delete
              </Button>
              {isDeleting && <CircularProgress size={20} />}
            </DeleteButtonRow>
          )}
        </ModalWrapper>
      )}
    </Wrapper>
  );
};

export default IllustrationPNG;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #e4e4e4;
  border-radius: 4px;
  padding: 2px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin: 4px 4px 4px 10px;
`;

const ImgPng = styled.img`
  width: 250px;
  height: 190px;
  object-fit: contain;
`;

const NoImageText = styled.div`
  width: 250px;
  height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #90a4ae;
  font-size: 14px;
  background-color: #f5f5f5;
`;

const StageIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StageBox = styled.div<{ active: boolean; completed: boolean; stageColor: string; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 2px solid ${(props) => (props.disabled ? '#e0e0e0' : props.completed ? props.stageColor : '#cccccc')};
  background-color: ${(props) => (props.disabled ? '#f0f0f0' : props.completed ? props.stageColor : '#f5f5f5')};
  color: ${(props) => (props.disabled ? '#bdbdbd' : props.active ? '#ffffff' : props.completed ? '#ffffff' : '#999999')};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : props.completed ? 'pointer' : 'default')};
  user-select: none;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.5 : props.active ? 1 : props.completed ? 0.8 : 0.4)};

  &:hover {
    ${(props) =>
      !props.disabled &&
      props.completed &&
      `
      transform: scale(1.1);
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      opacity: 1;
    `}
  }
`;

const UploadDescriptionStyled = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 12px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
`;

const DeleteButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const Icon = styled(FontAwesomeIcon)`
  margin-right: 6px;
  cursor: pointer;
  color: #607d8b;

  &:hover {
    color: #455a64;
  }
`;

const SharedIcon = styled(FontAwesomeIcon)`
  margin-right: 6px;
  color: #2196f3;
  cursor: help;
`;

const EditorRowStyled = styled(EditorRow)`
  justify-content: flex-start;
  margin-top: 10px;
`;

const FileUploadStyled = styled(FileUpload)`
  margin-right: 5px;
`;

